import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { realShopifyAPI, RealProduct } from '../services/realShopifyAPI';
import { MockProduct } from '../services/devShopifyAPI';

type Product = RealProduct | MockProduct;

const CATEGORY_DEFINITIONS = [
  { key: 'glossy', label: '光沢紙', keywords: ['光沢紙', '光沢', 'グロッシー'] },
  { key: 'matte', label: 'マット紙', keywords: ['マット紙', 'マット', '微粒面', 'ラスター'] },
  { key: 'washi', label: '和紙', keywords: ['和紙'] },
  { key: 'canvas', label: 'キャンバス', keywords: ['キャンバス'] },
  { key: 'others', label: 'その他', keywords: [] },
] as const;

type CategoryKey = typeof CATEGORY_DEFINITIONS[number]['key'];

const extractTags = (product: Product): string[] => {
  if (!product.tags) return [];
  if (Array.isArray(product.tags)) {
    return product.tags.map(tag => String(tag).trim()).filter(Boolean);
  }
  return String(product.tags)
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
};

const getCategoriesFromTags = (product: Product): CategoryKey[] => {
  const normalizedTags = extractTags(product).map(tag => tag.toLowerCase());

  const matchedCategories = CATEGORY_DEFINITIONS.filter(category => {
    if (category.key === 'others') return false;
    return category.keywords.some(keyword =>
      normalizedTags.some(tag => tag.includes(keyword.toLowerCase()))
    );
  }).map(category => category.key as CategoryKey);

  if (matchedCategories.length > 0) {
    return matchedCategories;
  }

  return ['others'];
};

interface ProductSelectorProps {
  onProductSelect: (product: Product) => void;
  onClose: () => void;
  filterType?: 'paper' | 'frame' | 'mat-processing' | 'panel' | 'canvas'; // フィルタリングタイプを追加
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ onProductSelect, onClose, filterType = 'paper' }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let productData: Product[];

      console.log(`🎯 [ProductSelector] filterType: ${filterType}`);

      // 実ストアから全商品を取得（ページネーション対応）
      console.log(`📡 [ProductSelector] 実ストアから全商品を取得中（ページネーション対応）...`);
      productData = await realShopifyAPI.getAllProducts();
      console.log(`✅ [ProductSelector] 実ストアから ${productData.length} 件の全商品を取得`);

      console.log(`📦 [ProductSelector] 取得した生の商品データ (${productData.length}件):`, productData);

      console.log(`🔍 [ProductSelector] filterType: ${filterType}`);
      console.log(`🔍 [ProductSelector] 全商品数: ${productData.length}`);

      // 全商品のタグ情報を表示
      productData.forEach((product, index) => {
        console.log(`📋 [ProductSelector] 商品${index + 1}: "${product.title}" - タグ:`, product.tags);
      });

      // フィルタリングタイプに応じて商品をフィルタリング
      console.log(`🔄 [ProductSelector] ${filterType}フィルタリング開始 - 対象商品数: ${productData.length}`);

      const filteredProducts = productData.filter(product => {
        // 下書きの商品は表示しない
        if ('status' in product && product.status !== 'active') {
          return false;
        }

        const tags = Array.isArray(product.tags) ? product.tags : [product.tags].filter(Boolean);
        console.log(`🔍 [ProductSelector] 商品 "${product.title}" - タグ:`, tags);

        if (filterType === 'paper') {
          // 用紙選択: 商品タグに「用紙」が入っている商品のみ
          return tags.some(tag => tag.includes('用紙'));
        } else if (filterType === 'frame') {
          // 額装&加工選択: 以下の商品タイトルの商品のみ
          const allowedTitles = [
            '木製フレーム額入れ',
            'アルミ製フレーム額入れ',
            '木製フレーム＆ブックマット加工',
            '木製フレーム＆オーバーマット加工',
            'アルミ製フレーム＆ブックマット加工',
            'アルミ製フレーム＆オーバーマット加工'
          ];
          return allowedTitles.includes(product.title);
        } else if (filterType === 'mat-processing') {
          // マット加工選択: 以下の商品タイトルの商品のみ
          const allowedTitles = [
            'ブックマット加工',
            'オーバーマット加工'
          ];
          return allowedTitles.includes(product.title);
        } else if (filterType === 'panel') {
          // パネル加工選択: 以下の商品タイトルの商品のみ
          const allowedTitles = [
            '木製パネル加工',
            'スチレンパネル加工'
          ];
          return allowedTitles.includes(product.title);
        } else if (filterType === 'canvas') {
          // キャンバス選択: 以下の商品タイトルの商品のみ
          const allowedTitles = [
            'キャンバス木枠張り'
          ];
          return allowedTitles.includes(product.title);
        }

        return true; // フィルタリングなし
      });

      console.log(`📊 [ProductSelector] ${filterType}フィルタ後の商品数: ${filteredProducts.length}`);

      setProducts(filteredProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : '商品の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const groupedProducts = useMemo(() => {
    const groups = CATEGORY_DEFINITIONS.reduce<Record<CategoryKey, Product[]>>((acc, category) => {
      acc[category.key] = [];
      return acc;
    }, {} as Record<CategoryKey, Product[]>);

    products.forEach((product) => {
      const categories = getCategoriesFromTags(product);
      categories.forEach(categoryKey => {
        groups[categoryKey].push(product);
      });
    });

    return groups;
  }, [products]);

  const handleCategoryJump = (category: CategoryKey) => {
    const element = document.getElementById(`category-${category}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleProductSelect = (product: Product) => {
    onProductSelect(product);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">商品を選択してください</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-800">
              <strong>エラー:</strong> {error}
            </div>
          </div>
        )}

        {/* 商品一覧 */}
        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              商品を読み込み中...
            </div>
          ) : Object.values(groupedProducts).every(items => items.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              商品が見つかりません
            </div>
          ) : (
            <>
              <div className="sticky top-0 z-10 bg-white py-2 mb-2 border-b border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_DEFINITIONS.map(category => {
                    const hasItems = groupedProducts[category.key]?.length > 0;
                    return (
                      <button
                        key={category.key}
                        type="button"
                        onClick={() => handleCategoryJump(category.key)}
                        disabled={!hasItems}
                        className={`px-3 py-1 rounded-full text-sm border transition ${hasItems
                          ? 'border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        {category.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-6">
                {CATEGORY_DEFINITIONS.map(category => {
                  const items = groupedProducts[category.key];
                  if (!items || items.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category.key} id={`category-${category.key}`}>
                      <div className="flex items-center mb-3 scroll-mt-20">
                        <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                          {category.label}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {items.length}件
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {items.map(product => (
                          <div
                            key={product.id}
                            className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div className="flex space-x-4">
                              <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                {product.images && product.images.length > 0 ? (
                                  <img
                                    src={product.images[0].src}
                                    alt={product.images[0].alt}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    画像なし
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {product.title}
                                </h3>
                                <p className="text-sm text-gray-600 truncate">
                                  {product.handle}
                                </p>
                                {product.variants && product.variants.length > 0 && (
                                  <div className="mt-1">
                                    <span className="text-lg font-bold text-green-600">
                                      ¥{product.variants[0].price}〜
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* フッター */}
        <div className="mt-4 pt-4 border-t flex justify-end">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelector;
